/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

const BUTTONS = [
	["AC", "DEL", "%", "/"],
	["7", "8", "9", "*"],
	["4", "5", "6", "-"],
	["1", "2", "3", "+"],
	["0", ".", "="]
];

const isOperator = (value) => ["+", "-", "*", "/", "%"].includes(value);

function sanitizeExpression(expr) {
	if (!expr) return "";
	const trimmed = expr.replace(/\s+/g, "").replace(/%/g, "/100");
	if (!/^[0-9+\-*/.()]+$/.test(trimmed)) return "";
	return trimmed;
}

function evaluateExpression(expr) {
	const safe = sanitizeExpression(expr);
	if (!safe) return "Error";
	try {
		const result = Function(`"use strict"; return (${safe})`)();
		if (Number.isFinite(result)) return String(result);
		return "Error";
	} catch (error) {
		return "Error";
	}
}

function Calculator({ onClose }) {
	const [expression, setExpression] = useState("0");
	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

	const handleMouseDown = (e) => {
		// Only drag from the header
		if (!e.target.closest(".calculator-header")) return;
		
		setIsDragging(true);
		setDragOffset({
			x: e.clientX - position.x,
			y: e.clientY - position.y,
		});
	};

	const handleMouseMove = (e) => {
		if (!isDragging) return;
		
		setPosition({
			x: e.clientX - dragOffset.x,
			y: e.clientY - dragOffset.y,
		});
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	useEffect(() => {
		if (isDragging) {
			document.addEventListener("mousemove", handleMouseMove);
			document.addEventListener("mouseup", handleMouseUp);
			return () => {
				document.removeEventListener("mousemove", handleMouseMove);
				document.removeEventListener("mouseup", handleMouseUp);
			};
		}
	}, [isDragging, dragOffset]);

	const appendValue = (value) => {
		if (value === "AC") {
			setExpression("0");
			return;
		}
		if (value === "DEL") {
			setExpression((prev) => (prev.length <= 1 ? "0" : prev.slice(0, -1)));
			return;
		}
		if (value === "=") {
			setExpression((prev) => evaluateExpression(prev));
			return;
		}

		setExpression((prev) => {
			if (prev === "Error") return value;
			if (prev === "0" && !isOperator(value) && value !== ".") return value;
			if (isOperator(value) && isOperator(prev.slice(-1))) {
				return prev.slice(0, -1) + value;
			}
			return prev + value;
		});
	};

	useEffect(() => {
		const onKeyDown = (event) => {
			const { key } = event;
			if (key === "Escape") {
				onClose?.();
				return;
			}
			if (key === "Enter" || key === "=") {
				event.preventDefault();
				appendValue("=");
				return;
			}
			if (key === "Backspace") {
				event.preventDefault();
				appendValue("DEL");
				return;
			}
			if (key === "Delete") {
				appendValue("AC");
				return;
			}
			if (/^[0-9]$/.test(key)) {
				appendValue(key);
				return;
			}
			if (["+", "-", "*", "/", "%", "."].includes(key)) {
				appendValue(key);
			}
		};

		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [appendValue, onClose]);

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
			<div className="absolute inset-0 bg-black/50 pointer-events-auto" onClick={onClose} />
			<div
				role="dialog"
				aria-modal="true"
				className="relative w-full max-w-sm mx-4 rounded-xl bg-[#0f172a] text-slate-100 shadow-2xl pointer-events-auto cursor-move"
				style={{
					transform: `translate(${position.x}px, ${position.y}px)`,
					transition: isDragging ? "none" : "transform 0.1s ease-out",
				}}
			>
				<div 
					className="calculator-header flex items-center justify-between border-b border-slate-700/60 px-4 py-3 cursor-grab active:cursor-grabbing"
					onMouseDown={handleMouseDown}
				>
					<h3 className="text-base font-semibold text-slate-100">Calculator</h3>
					<button
						onClick={onClose}
						aria-label="Close calculator"
						className="rounded px-2 py-1 text-sm text-slate-300 hover:bg-slate-800 cursor-pointer"
					>
						Close
					</button>
				</div>
				<div className="px-4 pt-4">
					<div className="rounded-lg bg-black/60 px-3 py-4 text-right text-2xl font-semibold text-slate-100">
						{expression}
					</div>
				</div>
				<div className="grid grid-cols-4 gap-2 p-4">
					{BUTTONS.flat().map((value) => {
						const isWide = value === "0";
						const isAction = value === "AC" || value === "DEL";
						const isEqual = value === "=";
						return (
							<button
								key={value}
								onClick={() => appendValue(value)}
								className={`h-12 rounded-lg text-base font-semibold shadow-sm transition
									${isWide ? "col-span-2" : ""}
									${isEqual ? "bg-cyellow text-cblack" : "bg-slate-800 text-slate-100"}
									${isAction ? "text-rose-300" : ""}
									${!isEqual ? "hover:bg-slate-700" : "hover:bg-cyellow/90"}
								`}
							>
								{value}
							</button>
						);
					})}
				</div>
			</div>
		</div>
	);
}

export default Calculator;
